// "Find Your Perfect Website Package" — questionnaire config + scoring.
// Scoring is intentionally data-driven so it's easy to adjust later:
// each option carries a `score`; `force: "growth"` forces the Growth package.

export const PACKAGE_RESULTS = {
  minimum: {
    key: "minimum",
    name: "Minimum",
    price: "£2,495",
    description:
      "Your business would benefit from a professional, high-quality website designed to establish credibility, showcase your services and projects, and make it easy for potential customers to contact you.",
  },
  flagship: {
    key: "flagship",
    name: "Flagship",
    price: "£4,495",
    description:
      "Your business would benefit from a conversion-focused website designed to generate higher-quality enquiries, streamline your quotation process and automate key customer interactions.",
  },
  growth: {
    key: "growth",
    name: "Growth",
    price: "£7,995",
    description:
      "Your business requires a more advanced digital solution combining a premium website, custom CRM functionality, sophisticated automations and integrations designed to support your operations and future growth.",
  },
};

// Score thresholds — adjust freely.
export const THRESHOLDS = { minimumMax: 2, flagshipMax: 8 }; // 0–2 Minimum, 3–8 Flagship, 9+ Growth

export const QUIZ_STEPS = [
  {
    title: "Your Business",
    questions: [
      {
        id: "business_type",
        label: "What type of business do you operate?",
        type: "single",
        options: [
          { value: "construction", label: "Construction / Building" },
          { value: "renovations", label: "Renovations / Home Improvements" },
          { value: "trades", label: "Electrical / Plumbing / HVAC" },
          { value: "specialist", label: "Roofing / Landscaping / Specialist Trade" },
          { value: "other", label: "Other" },
        ],
      },
      {
        id: "business_size",
        label: "How big is your business?",
        type: "single",
        options: [
          { value: "sole", label: "Sole trader / 1–2 employees", score: 0 },
          { value: "small", label: "Small business / 3–10", score: 0 },
          { value: "established", label: "Established business / 11–30", score: 1 },
          { value: "larger", label: "Larger company / 31+", score: 2 },
        ],
      },
    ],
  },
  {
    title: "Your Current Website",
    questions: [
      {
        id: "situation",
        label: "What best describes your situation?",
        type: "single",
        options: [
          { value: "none", label: "I don't have a website", score: 0 },
          { value: "redesign", label: "I have one that needs redesigning", score: 0 },
          { value: "low_enquiries", label: "I have one that doesn't generate enough enquiries", score: 0 },
          { value: "advanced", label: "I need a more advanced website and business system", score: 3 },
        ],
      },
    ],
  },
  {
    title: "Your Requirements",
    questions: [
      {
        id: "objective",
        label: "What is your main objective?",
        type: "single",
        options: [
          { value: "presence", label: "Establish a professional online presence", score: 0 },
          { value: "enquiries", label: "Generate more qualified enquiries", score: 2 },
          { value: "automate", label: "Automate quoting and customer enquiries", score: 3 },
          { value: "system", label: "Build an advanced digital system for my business", score: 5 },
        ],
      },
      {
        id: "features",
        label: "Which features would you benefit from?",
        hint: "Choose as many as apply",
        type: "multi",
        options: [
          { value: "website_galleries", label: "Modern website with services and project galleries", score: 0 },
          { value: "quotation", label: "Multi-step quotation / qualification system", score: 2 },
          { value: "seo_landing", label: "SEO restructuring and lead-generation landing pages", score: 1 },
          { value: "followups", label: "Automated email and SMS follow-ups", score: 2 },
          { value: "crm", label: "CRM to manage enquiries and customers", score: 3 },
          { value: "integrations", label: "AI chatbot or custom integrations", score: 3, force: "growth" },
        ],
      },
    ],
  },
  {
    title: "Your Investment",
    questions: [
      {
        id: "investment",
        label: "What investment range are you considering?",
        type: "single",
        options: [
          { value: "2495_4494", label: "£2,495 – £4,494", score: 0 },
          { value: "4495_7994", label: "£4,495 – £7,994", score: 0 },
          { value: "7995_plus", label: "£7,995+", score: 0 },
          { value: "unsure", label: "I'm unsure and would like advice", score: 0 },
        ],
      },
      {
        id: "timeline",
        label: "When would you like to launch?",
        type: "single",
        options: [
          { value: "2_4_weeks", label: "Within 2–4 weeks", score: 0 },
          { value: "1_2_months", label: "1–2 months", score: 0 },
          { value: "3_months", label: "3 months", score: 0 },
          { value: "flexible", label: "I'm flexible", score: 0 },
        ],
      },
    ],
  },
  {
    title: "Your Goals",
    questions: [
      {
        id: "goal",
        label: "What would make this a success?",
        type: "single",
        options: [
          { value: "credible", label: "A more credible online presence", score: 0 },
          { value: "higher_value", label: "More enquiries for higher-value projects", score: 0 },
          { value: "less_time", label: "Less time spent qualifying and following up leads", score: 0 },
          { value: "scale", label: "A complete system to manage and scale our operations", score: 3 },
        ],
      },
    ],
  },
];

const findOption = (qid, value) => {
  for (const step of QUIZ_STEPS) {
    const q = step.questions.find((x) => x.id === qid);
    if (q) return q.options.find((o) => o.value === value);
  }
  return null;
};

// Returns { score, packageKey }
export const scoreAnswers = (answers) => {
  let score = 0;
  let force = null;
  for (const [qid, val] of Object.entries(answers || {})) {
    const values = Array.isArray(val) ? val : [val];
    for (const v of values) {
      const opt = findOption(qid, v);
      if (!opt) continue;
      score += opt.score || 0;
      if (opt.force) force = opt.force;
    }
  }
  let packageKey = "minimum";
  if (score > THRESHOLDS.flagshipMax) packageKey = "growth";
  else if (score > THRESHOLDS.minimumMax) packageKey = "flagship";
  if (force) packageKey = force; // e.g. custom integrations always → Growth
  return { score, packageKey };
};
